import {isArray, isString} from 'lodash';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {InlineBannerTypesEnum} from '../../../components/inlineBanner/inlineBanner';
import {WithClientId} from '../commons/clientIdModel';
import {Parameter} from '../commons/parameterModel';
import {Expression, ExpressionDryRunSnapshot} from './expressionModel';
import {InferredType} from './infer/expressionResultModel';
import {TransformerIdsEnum} from './infer/internal';
import type {AtomParams, PreAtom} from './iotsCodecs';

// Re-export types.
export type {AtomParams, PreAtom} from './iotsCodecs';

/*
 * Atom.
 * An expression is made by a chain of atoms. Each atom transforms the previous result with a transformer + its embedded params.
 */

export enum AtomParamTypesEnum {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  ENUM = 'enum',
  TEAMMATE_LIST = 'teammateList',
}

export interface UninferredAtom {
  previousInfer: InferredType;
  transform: TransformerIdsEnum;
  params: AtomParams;
  functionAtoms?: ReadonlyArray<ClientAtom>;
  isValid: boolean;
  errorMessage?: string;
  errorType?: InlineBannerTypesEnum;
}

export interface Atom extends UninferredAtom {
  inferredType: InferredType;
  dryRun?: ExpressionDryRunSnapshot;
}

export type FunctionAtom = Atom & {
  functionAtoms: ReadonlyArray<ClientAtom>;
};

export type ClientAtom = WithClientId<Atom>;
export type ClientFunctionAtom = WithClientId<FunctionAtom>;

export function isClientAtom(atom: any): atom is ClientAtom {
  return Boolean(atom && isString(atom.transform) && isString(atom.clientId));
}

export function isFunctionAtom(atom: Atom): atom is FunctionAtom {
  return isArray(atom.functionAtoms);
}

export function serializeAtom(atom: ClientAtom): PreAtom {
  return {
    transform: atom.transform,
    params: atom.params,
    functionAtoms: atom.functionAtoms ? atom.functionAtoms.filter(isDefined).map(serializeAtom) : undefined,
  };
}

function isInAtom(atom: ClientAtom, parentAtom: ClientAtom): boolean {
  if (!isFunctionAtom(parentAtom)) {
    return false;
  }

  return parentAtom.functionAtoms.some((a) => {
    if (atom.clientId === a.clientId) {
      return true;
    }

    if (isFunctionAtom(a)) {
      return isInAtom(atom, a);
    }

    return false;
  });
}

export function isInFunctionAtom(atom: ClientAtom, expressions: ReadonlyArray<Expression>): boolean {
  return expressions.some((expression) => expression.atoms.some((parentAtom) => isInAtom(atom, parentAtom)));
}

export function buildDefaultAtomParams(parameters: ReadonlyArray<Parameter>): AtomParams {
  const params: AtomParams = {};

  parameters.forEach((param) => {
    if (Parameter.isEnum(param) && param.default) {
      params[param.id] = param.default;
    }
  });

  return params;
}
